import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { DropdownMenuItem } from '../../../dropdownMenu/DropdownMenu'
import { RowActions } from '../../../listItem/rowActions/RowActions'
import { ListRow } from '../../../listItem/listRow/ListRow'
import { ListCard } from '../../../listItem/listCard/ListCard'
import { ListCardStripe } from '../../../../types/listing'
import { SimpleTable } from '../../../listing/simpleTable/SimpleTable'

import { PurchasedPass, PurchasedPassStatus } from '../../../../types/booking/booking'
import { checkoutPath, formatMinutes } from '../../../../utils/booking/booking'
import { toDisplayLocalDate } from '../../../../utils/dateUtils'

const STATUS_BADGES: Record<PurchasedPassStatus, string> = {
  [PurchasedPassStatus.ACTIVE]: 'badge-success',
  [PurchasedPassStatus.PENDING_PAYMENT]: 'badge-warning',
  [PurchasedPassStatus.EXPIRED]: 'badge-secondary',
  [PurchasedPassStatus.VOID]: 'badge-danger',
  [PurchasedPassStatus.USED_UP]: 'badge-secondary',
}

const STATUS_STRIPES: Record<PurchasedPassStatus, ListCardStripe> = {
  [PurchasedPassStatus.ACTIVE]: 'success',
  [PurchasedPassStatus.PENDING_PAYMENT]: 'warning',
  [PurchasedPassStatus.EXPIRED]: 'neutral',
  [PurchasedPassStatus.VOID]: 'danger',
  [PurchasedPassStatus.USED_UP]: 'neutral',
}

const OVER_STATUSES = [PurchasedPassStatus.EXPIRED, PurchasedPassStatus.VOID, PurchasedPassStatus.USED_UP]

/**
 * Passes a customer owns. Available minutes exclude minutes reserved by a checkout in progress; minutes of an expired
 * pass are no longer usable.
 */
export const PurchasedPassList = ({ passes }: { passes: PurchasedPass[] }) => {
  const { t, i18n } = useTranslation('passes')
  const navigate = useNavigate()

  /** A purchase whose payment is still open can be resumed on its checkout page (same hold, same deadline). */
  const pendingCheckout = (pass: PurchasedPass) =>
    pass.status === PurchasedPassStatus.PENDING_PAYMENT && pass.checkoutId ? pass.checkoutId : undefined

  const openCheckout = (pass: PurchasedPass) => {
    const checkoutId = pendingCheckout(pass)
    if (checkoutId) {
      navigate(checkoutPath(checkoutId))
    }
  }

  const menuItems = (pass: PurchasedPass): DropdownMenuItem[] =>
    pendingCheckout(pass)
      ? [
          {
            key: 'pay',
            label: t('passes.mine.continue-payment'),
            icon: 'arrowR',
            onClick: () => openCheckout(pass),
          },
        ]
      : []

  /** Same "more actions" menu as every other list; every row keeps the cell so dividers stay aligned. */
  const actions = (pass: PurchasedPass) => <RowActions label={t('passes.mine.more-actions')} items={menuItems(pass)} />


  const source = (pass: PurchasedPass) => t(`passes.mine.source.${pass.source.toLowerCase()}`)

  const status = (pass: PurchasedPass) => (
    <span className={`badge ${STATUS_BADGES[pass.status]}`}>
      {t(`passes.mine.status.${pass.status.toLowerCase()}`)}
    </span>
  )

  const minutesValue = (pass: PurchasedPass) => (
    <>
      <div className="mono fw-semibold">
        {formatMinutes(pass.availableMinutes)} / {formatMinutes(pass.totalMinutes)}
      </div>
      {pass.remainingMinutes > pass.availableMinutes && pass.status === PurchasedPassStatus.ACTIVE && (
        <div className="tiny muted">
          {t('passes.mine.reserved', { minutes: formatMinutes(pass.remainingMinutes - pass.availableMinutes) })}
        </div>
      )}
    </>
  )

  const minutes = (pass: PurchasedPass) => <div className="text-center">{minutesValue(pass)}</div>

  const expires = (pass: PurchasedPass) =>
    pass.expiresAt ? toDisplayLocalDate(pass.expiresAt, i18n.language) : '—'

  return (
    <>
      {/* The table shows from 1400px (the shared listing breakpoint, like the booking history); cards below. */}
      <div>
        <SimpleTable
          columns={[
            { key: 'name', label: t('passes.mine.columns.name'), align: 'center' },
            { key: 'source', label: t('passes.mine.columns.source'), align: 'center' },
            { key: 'minutes', label: t('passes.mine.columns.minutes'), align: 'center' },
            { key: 'expires', label: t('passes.mine.columns.expires'), align: 'center' },
            { key: 'status', label: t('passes.mine.columns.status'), align: 'center' },
            { key: 'actions', label: t('passes.mine.columns.actions'), align: 'center' },
          ]}
        >
          {passes.map(pass => (
            <ListRow
              key={pass.id}
              onClick={pendingCheckout(pass) ? () => openCheckout(pass) : undefined}
              title={pendingCheckout(pass) ? t('passes.mine.continue-payment') : undefined}
              cells={[
                { key: 'name', className: 'fw-semibold text-nowrap text-center', content: pass.name },
                { key: 'source', className: 'text-center', content: source(pass) },
                { key: 'minutes', content: minutes(pass) },
                { key: 'expires', className: 'mono text-center', content: expires(pass) },
                { key: 'status', className: 'text-center', content: status(pass) },
              ]}
              actions={actions(pass)}
            />
          ))}
        </SimpleTable>
      </div>
      <div className="row g-3 d-xxl-none">
        {passes.map(pass => (
          <div key={pass.id} className="col-12 col-xl-6">
            <ListCard
              stripe={STATUS_STRIPES[pass.status]}
              title={pass.name}
              subtitle={source(pass)}
              status={status(pass)}
              menu={actions(pass)}
              muted={OVER_STATUSES.includes(pass.status)}
              onOpen={pendingCheckout(pass) ? () => openCheckout(pass) : undefined}
              openLabel={t('passes.mine.continue-payment')}
              fields={[
                { key: 'minutes', label: t('passes.mine.columns.minutes'), value: minutesValue(pass) },
                { key: 'expires', label: t('passes.mine.columns.expires'), value: expires(pass) },
              ]}
            />
          </div>
        ))}
      </div>
    </>
  )
}
